import React, { useState, useEffect } from "react";
import {
  Users,
  Home,
  Baby,
  TrendingUp,
  Activity,
  MapPin,
  Loader2,
  AlertCircle,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const UserAnalyticsPanel = ({ getAccessToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = getAccessToken();
        const response = await fetch(`${API_URL}/v2/admin/analytics/users/overview`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error("Error fetching user analytics:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessToken]);

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
        <div className="flex items-center gap-2 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading user analytics...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
        <div className="flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>Error loading user analytics</span>
        </div>
        <p className="text-red-300 text-sm mt-2">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { summary, households, location, engagement, dauTrend } = data;

  // Format chart data
  const chartData = dauTrend.map((day) => ({
    date: new Date(day.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    users: day.active_users,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">User Engagement</h3>
            <p className="text-sm text-gray-400">Platform usage and growth</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Activity className="w-4 h-4" />
          Live data
        </div>
      </div>

      {/* Total Users Card */}
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-4xl font-bold text-white">{summary.totalUsers.toLocaleString()}</div>
            <div className="text-sm text-gray-400 mt-1">Total Registered Users</div>
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1 text-green-400 text-sm">
                <TrendingUp className="w-4 h-4" />
                +{summary.newUsers.thisWeek} this week
              </div>
              <div className="text-sm text-gray-500">
                +{summary.newUsers.thisMonth} this month
              </div>
            </div>
          </div>
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500/20 to-teal-500/20 flex items-center justify-center border border-green-500/30">
            <Users className="w-10 h-10 text-green-400" />
          </div>
        </div>
      </div>

      {/* Active Users Metrics */}
      <div>
        <h4 className="text-white font-medium mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-indigo-400" />
          Active Users
        </h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700 text-center">
            <div className="text-2xl font-bold text-white">{summary.activeUsers.last7Days}</div>
            <div className="text-xs text-gray-400 mt-1">Last 7 days</div>
          </div>
          <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700 text-center">
            <div className="text-2xl font-bold text-indigo-400">{summary.activeUsers.last30Days}</div>
            <div className="text-xs text-gray-400 mt-1">Last 30 days</div>
          </div>
          <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700 text-center">
            <div className="text-2xl font-bold text-white">{summary.activeUsers.last90Days}</div>
            <div className="text-xs text-gray-400 mt-1">Last 90 days</div>
          </div>
        </div>
      </div>

      {/* DAU Chart */}
      {chartData.length > 0 && (
        <div>
          <h4 className="text-white font-medium mb-3">Daily Active Users (30 days)</h4>
          <div className="h-48 bg-gray-800/30 rounded-lg border border-gray-700 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#818cf8" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#818cf8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={10} tickLine={false} />
                <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1f2937",
                    border: "1px solid #374151",
                    borderRadius: "6px",
                  }}
                  labelStyle={{ color: "#9ca3af" }}
                  itemStyle={{ color: "#818cf8" }}
                />
                <Area
                  type="monotone"
                  dataKey="users"
                  name="Active Users"
                  stroke="#818cf8"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorUsers)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Households */}
      <div>
        <h4 className="text-white font-medium mb-3 flex items-center gap-2">
          <Home className="w-4 h-4 text-green-400" />
          Households
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Home className="w-4 h-4" />
              Total Households
            </div>
            <div className="text-2xl font-bold text-white">{households.totalHouseholds}</div>
            <div className="text-xs text-gray-400 mt-1">
              {households.usersWithHouseholds} users with children
            </div>
          </div>

          <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Baby className="w-4 h-4" />
              Total Children
            </div>
            <div className="text-2xl font-bold text-white">{households.totalChildren}</div>
            <div className="text-xs text-gray-400 mt-1">
              Avg {households.averageChildrenPerHousehold} per household
            </div>
          </div>
        </div>
      </div>

      {/* Location & Engagement */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
            <MapPin className="w-4 h-4" />
            Ann Arbor Users
          </div>
          <div className="text-2xl font-bold text-white">{location.annArborUsers}</div>
          <div className="text-xs text-gray-400 mt-1">
            {location.usersWithLocation > 0
              ? Math.round((location.annArborUsers / location.usersWithLocation) * 100)
              : 0}% of located users
          </div>
        </div>

        <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
            <Activity className="w-4 h-4" />
            Engaged Users
          </div>
          <div className="text-2xl font-bold text-white">{engagement.usersWithSavedActivities}</div>
          <div className="text-xs text-gray-400 mt-1">
            Have saved activities
          </div>
        </div>
      </div>

      {/* Activity Requests */}
      <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-white font-medium">Activity Requests</h4>
            <p className="text-sm text-gray-400 mt-1">
              {engagement.pendingActivityRequests} pending requests
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-indigo-400">{engagement.totalActivityRequests}</div>
            <div className="text-xs text-gray-400">total submitted</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAnalyticsPanel;
